import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/hooks/useSubscription";
import { useDevPlanOverride } from "@/hooks/useDevPlanOverride";
import { useIsAdmin } from "@/hooks/useAdmin";
import type { SubscriptionTier } from "@/hooks/useSubscription";
import { User, Save, Loader2, LogOut, CreditCard, ArrowRight, Settings2, Trash2, Bug } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import analytics from "@/lib/segment";

const Settings = () => {
  const [email, setEmail] = useState<string>("");
  const [fullName, setFullName] = useState<string>("");
  const [originalName, setOriginalName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { subscriptionTier, vehicleLimit, openCustomerPortal, isProOrAbove } = useSubscription();
  const { override, setOverride } = useDevPlanOverride();
  const { data: isAdmin } = useIsAdmin();
  const allTiers: SubscriptionTier[] = ["free", "pro", "scale", "fleet"];

  const handleSignOut = async () => {
    analytics.track("Signed Out");
    await supabase.auth.signOut();
    navigate("/");
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      analytics.track("Billing Portal Opened", {
        subscription_tier: subscriptionTier,
      });
      await openCustomerPortal();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to open billing portal",
        variant: "destructive",
      });
    } finally {
      setPortalLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      // Verify session exists before calling edge function
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error("Your session has expired. Please sign in again.");
      }

      // Track before deletion (analytics will be reset on sign out)
      analytics.track("Account Deleted", {
        subscription_tier: subscriptionTier,
      });

      const response = await supabase.functions.invoke("delete-account");
      
      if (response.error) {
        throw new Error(response.error.message || "Failed to delete account");
      }

      if (response.data?.error) {
        throw new Error(response.data.error);
      }

      toast({
        title: "Account deleted",
        description: "Your account has been permanently deleted.",
      });

      // Sign out and redirect
      await supabase.auth.signOut();
      navigate("/");
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete account",
        variant: "destructive",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        setEmail(user.email || "");
        
        const { data: profile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("user_id", user.id)
          .maybeSingle();
        
        if (profile?.full_name) {
          setFullName(profile.full_name);
          setOriginalName(profile.full_name);
        }
      }
      setLoading(false);
    };

    fetchUserData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to update your profile.",
        variant: "destructive",
      });
      setSaving(false);
      return;
    }

    const { error } = await supabase
      .from("profiles")
      .update({ full_name: fullName })
      .eq("user_id", user.id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } else {
      setOriginalName(fullName);
      analytics.track("Profile Updated");
      toast({
        title: "Success",
        description: "Your profile has been updated.",
      });
    }
    
    setSaving(false);
  };

  const hasChanges = fullName !== originalName;

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your account settings</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Profile Information
            </CardTitle>
            <CardDescription>
              Your personal account details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Email cannot be changed
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
                disabled={loading}
              />
            </div>

            <Button 
              onClick={handleSave} 
              disabled={!hasChanges || saving}
              className="mt-2"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Subscription
            </CardTitle>
            <CardDescription>
              Manage your subscription plan
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-medium">Current Plan</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant={subscriptionTier === "scale" ? "default" : subscriptionTier === "pro" ? "secondary" : "outline"}>
                    {subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {vehicleLimit === null ? "Unlimited vehicles" : `${vehicleLimit} vehicle${vehicleLimit !== 1 ? 's' : ''}`}
                  </span>
                </div>
              </div>
              <Button asChild variant="outline" onClick={() => analytics.track("Upgrade CTA Clicked", {
                source: "settings_subscription",
                current_tier: subscriptionTier,
                vehicle_limit: vehicleLimit,
              })}>
                <Link to="/app/upgrade">
                  Upgrade Plan
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            {isProOrAbove && (
              <div className="flex items-center justify-between pt-4 border-t">
                <div>
                  <p className="font-medium">Billing & Payment</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Update payment method, view invoices, or cancel subscription
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleManageBilling}
                  disabled={portalLoading}
                >
                  {portalLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Settings2 className="mr-2 h-4 w-4" />
                      Manage Billing
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LogOut className="h-5 w-5" />
              Account Actions
            </CardTitle>
            <CardDescription>
              Manage your session and account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="font-medium">Sign Out</p>
                <p className="text-sm text-muted-foreground">
                  End your current session
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={handleSignOut}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t">
              <div>
                <p className="font-medium text-destructive">Delete Account</p>
                <p className="text-sm text-muted-foreground">
                  Permanently delete your account and all data
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={deleteLoading}>
                    {deleteLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Account
                      </>
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete your account, 
                      cancel any active subscriptions, and remove all your data including vehicles, 
                      trips, expenses, and maintenance records.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleDeleteAccount}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Yes, delete my account
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>

        {isAdmin && (
          <Card className="border-dashed border-warning/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-warning">
                <Bug className="h-5 w-5" />
                Dev Plan Override
              </CardTitle>
              <CardDescription>
                Simulate a different subscription tier without changing Stripe. This only affects the UI.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={override === null ? "default" : "outline"}
                  size="sm"
                  onClick={() => setOverride(null)}
                >
                  Off (use real plan)
                </Button>
                {allTiers.map((tier) => (
                  <Button
                    key={tier}
                    variant={override === tier ? "default" : "outline"}
                    size="sm"
                    onClick={() => setOverride(tier)}
                  >
                    {tier.charAt(0).toUpperCase() + tier.slice(1)}
                  </Button>
                ))}
              </div>
              {override && (
                <p className="text-xs text-warning mt-3">
                  Currently overriding to <span className="font-semibold">{override}</span>. The page will reload when changed.
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
};

export default Settings;
